import {
  fixSureFlatPath,
  rewriteTrimbleTileUrl,
} from '../utils/trimble-tile-url';
import {
  SplatDownloadFields,
  TilesetJson,
  TilesetTile,
} from './splats-url-utils.types';

const PLY_PATH_RE = /\.ply$/i;
const JSON_PATH_RE = /\.json$/i;
const LOD_NAME_RE = /-L(\d+)/i;
const SURE_JSON_TILE_RE = /^s_[^/]+\.json$/i;

export function normalizeResourcePath(value: string): string {
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.pathname;
    }
  } catch (error: unknown) {
    // `new URL` throws TypeError for relative tileset URIs such as
    // ../Gaussian_Splats-L1.ply. Any other failure is unexpected.
    if (!(error instanceof TypeError)) {
      throw error;
    }
  }

  const withoutQuery = trimmed.split('?')[0] ?? trimmed;
  return (withoutQuery.split('#')[0] ?? withoutQuery).trim();
}

export function templateUrlFromDownloadUrl(downloadUrl: string): string {
  if (!downloadUrl.includes('*')) {
    throw new Error('downloadUrl must contain a * placeholder for tile paths');
  }
  return downloadUrl.replace('*', '/{path}');
}

export function resolveSplatTileUrls(splat: SplatDownloadFields): {
  tilesetUrl: string;
  templateUrl: string;
} {
  const templateUrl = templateUrlFromDownloadUrl(splat.downloadUrl);
  const tilesetUrl =
    splat.tilesetUrl ?? splat.downloadUrl.replace('*', '/tileset.json');

  return { tilesetUrl, templateUrl };
}

export function isPlyPath(value: string | undefined): boolean {
  if (value == null || value.length === 0) {
    return false;
  }

  return PLY_PATH_RE.test(normalizeResourcePath(value));
}

export function isExternalGsplatTilesetJson(
  value: string | undefined
): boolean {
  if (value == null || value.length === 0) {
    return false;
  }

  const path = normalizeResourcePath(value);
  if (!JSON_PATH_RE.test(path)) {
    return false;
  }

  const baseName = path.split('/').pop() ?? path;
  return !SURE_JSON_TILE_RE.test(baseName);
}

export function splatMemberUrl(
  memberPath: string,
  tilesetUrl: string,
  templateUrl?: string
): string {
  const normalizedPath = normalizeResourcePath(memberPath)
    .replace(/^\.\//, '')
    .replace(/^\//, '');

  if (templateUrl?.includes('{path}')) {
    return templateUrl.replace('{path}', fixSureFlatPath(normalizedPath));
  }

  const requestUrl = new URL(memberPath, tilesetUrl).href;
  const rewritten = rewriteTrimbleTileUrl(requestUrl, tilesetUrl, templateUrl);
  if (rewritten !== requestUrl) {
    return rewritten;
  }

  const resolved = new URL(memberPath, tilesetUrl);
  resolved.search = new URL(tilesetUrl).search;
  return resolved.toString();
}

interface PlyCandidate {
  readonly uri: string;
  readonly depth: number;
  readonly geometricError: number;
  readonly namedLod: number;
}

function contentUris(tile: TilesetTile): string[] {
  const uris: string[] = [];
  if (tile.content?.uri != null && tile.content.uri.length > 0) {
    uris.push(normalizeResourcePath(tile.content.uri));
  }
  for (const content of tile.contents ?? []) {
    if (content.uri != null && content.uri.length > 0) {
      uris.push(normalizeResourcePath(content.uri));
    }
  }
  return uris;
}

export function namedLodFromUri(uri: string): number {
  const match = LOD_NAME_RE.exec(uri);
  return match?.[1] == null ? -1 : Number(match[1]);
}

function collectPlyCandidates(
  tile: TilesetTile,
  depth: number,
  inheritedGeometricError: number,
  out: PlyCandidate[]
): void {
  const geometricError = tile.geometricError ?? inheritedGeometricError;

  for (const uri of contentUris(tile)) {
    if (isPlyPath(uri)) {
      out.push({
        uri,
        depth,
        geometricError,
        namedLod: namedLodFromUri(uri),
      });
    }
  }

  for (const child of tile.children ?? []) {
    collectPlyCandidates(child, depth + 1, geometricError, out);
  }
}

function isHigherLod(current: PlyCandidate, best: PlyCandidate): boolean {
  if (current.namedLod !== best.namedLod) {
    return current.namedLod > best.namedLod;
  }
  if (current.depth !== best.depth) {
    return current.depth > best.depth;
  }
  return current.geometricError < best.geometricError;
}

/** Highest-detail PLY in a tileset (named -Ln, else deepest / lowest geometricError). */
export function pickHighestLodPlyUri(tileset: TilesetJson): string | undefined {
  if (tileset.root == null) {
    return undefined;
  }

  const candidates: PlyCandidate[] = [];
  collectPlyCandidates(
    tileset.root,
    0,
    tileset.root.geometricError ?? Number.POSITIVE_INFINITY,
    candidates
  );

  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.reduce((best, current) =>
    isHigherLod(current, best) ? current : best
  ).uri;
}

export function collectExternalTilesetUris(tileset: TilesetJson): string[] {
  const uris: string[] = [];

  const walk = (tile: TilesetTile): void => {
    for (const uri of contentUris(tile)) {
      if (isExternalGsplatTilesetJson(uri)) {
        uris.push(uri);
      }
    }
    for (const child of tile.children ?? []) {
      walk(child);
    }
  };

  if (tileset.root != null) {
    walk(tileset.root);
  }

  return uris;
}
